import _ from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  CreatePortal,
  CustomerPortalApi,
  CustomerPortalRequestParams,
} from 'api/customer-portal/api';
import {
  CustomerPortalSettingsRequestAndResponse,
  CustomerPortalSettingsApi,
  CustomerPortalSettingsAlgorithm,
  CustomerPortalSettingsMechanism,
} from 'api/customer-portal/settings-api';
import { CustomerPortal } from 'api/customer-portal/types';
import { Project } from 'api/project';
import { getDynamicConditionFields } from 'data/templates/projects/utils';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { db } from 'workers/db';

export const useGetAllCustomerPortalsLight = () => {
  return useQuery('useGetAllCustomerPortalsLight', () =>
    CustomerPortalApi.getCustomerPortalsInAccount(),
  );
};

export const useGetCustomerPortalById = (portalId: number) => {
  return useQuery(
    ['useGetCustomerPortalById', portalId],
    () => CustomerPortalApi.getCustomerPortalById(portalId),
    { enabled: _.isFinite(portalId) },
  );
};

export const useGetCustomerPortalByIdIDB = (portalId: number) => {
  return useQuery(
    ['useGetCustomerPortalByIdIDB', portalId],
    async () => {
      const arr = await db.customerPortal
        .where({ customerPortalId: portalId })
        .toArray();

      return JSON.parse(JSON.stringify(arr?.[0])) as CustomerPortal;
    },
    { enabled: _.isFinite(portalId) },
  );
};

export const useGetCustomerPortalByIdFromIDB = (portalId: number) => {
  const q = useGetCustomerPortalById(portalId);
  const i = useGetCustomerPortalByIdIDB(portalId);

  const iDate = moment(i?.data?.updatedAt);
  const qDate = moment(q?.data?.updatedAt);

  if (i?.isLoading && q?.isLoading) {
    return { query: i, isDirty: false };
  }

  // If the portal is not available in IDB
  if (!i?.data) {
    return { query: q, isDirty: false };
  }
  // In some case the CP is deleted
  if (!q?.data) {
    return { query: q, isDirty: false };
  }

  const isAfter = iDate.isAfter(qDate);

  if (isAfter) {
    return { query: i, isDirty: true };
  }

  return { query: q, isDirty: false };
};

export const useCreateCustomerPortal = () => {
  return useMutation((p: CreatePortal) =>
    CustomerPortalApi.createCustomerPortal(p),
  );
};

export const useUpdateCustomerPortal = () => {
  return useMutation(
    (p: { portalId: number; payload: Partial<CreatePortal> }) => {
      return CustomerPortalApi.updateCustomerPortal(p.portalId, p.payload);
    },
  );
};

export const useDeleteCustomerPortal = () => {
  return useMutation((p: number) =>
    CustomerPortalApi.deleteCustomerPortalById(p),
  );
};

export const useGetCustomerPortalForPreview = ({
  portalId,
  projectId,
}: {
  portalId: number;
  projectId: number;
}) => {
  const query = useQuery(
    ['useGetCustomerPortalForPreview', projectId, portalId],
    () =>
      CustomerPortalApi.getCustomerPortalForProjectForPreview({
        projectId,
        portalId,
      }),
  );

  return query;
};

export const useGetPortalForProject = (projectId: number) => {
  return useQuery(['useGetPortalForProject', projectId], () =>
    CustomerPortalApi.getPortalForProject(projectId),
  );
};

export const useGetInputFieldsForWidget = (
  payload?: CustomerPortalRequestParams & { widgetUuid: string },
) => {
  return useQuery(
    ['widget-input-fields', payload?.widgetType, payload?.widgetUuid],
    () => (payload ? CustomerPortalApi.getInputFields(payload) : undefined),
    {
      enabled: !!payload,
    },
  );
};

export const useGetCustomerPortalSettings = () => {
  return useQuery('customer-portal-settings', () =>
    CustomerPortalSettingsApi.getCustomerPortalsSettingsForAccount(),
  );
};

export const useUpdateCustomerPortalSettings = () => {
  const { refetch } = useGetCustomerPortalSettings();

  return useMutation(
    (p: CustomerPortalSettingsRequestAndResponse) =>
      CustomerPortalSettingsApi.updateCustomerPortalsSettingsForAccount(p),
    {
      onSuccess: () => {
        refetch();
      },
    },
  );
};

export const useGetDynamicConditionFieldsUsedInPortal = (
  payload: CustomerPortalRequestParams,
) => {
  return useQuery(
    [
      'useGetDynamicConditionFieldsUsedInPortal',
      payload?.portal?.customerPortalId,
    ],
    () => CustomerPortalApi.getDynamicConditionFieldsUsedInPortal(payload),
    { keepPreviousData: true },
  );
};

export const useGetLivePreview = () => {
  return useQuery(
    ['useGetCustomerPortalForPreviewInBuilder, live'],
    async () => {
      const x = await db.liveCustomerPortal.where('id').equals('new').first();

      return CustomerPortalApi.getCustomerPortalForPreview({
        portal: x?.portal,
        project: x?.project,
        projectId: x?.projectId,
      });
    },
    {
      refetchOnMount: true,
      keepPreviousData: true,
    },
  );
};

export const useGetCustomerPortalSettingsAlgorithmOptions = () => {
  const translate = useTranslate();

  return Object.values(CustomerPortalSettingsAlgorithm).map((key) => ({
    value: key,
    label: translate(
      `customer_portal_settings.algorithm.${key}.label` as TranslationKey,
    ),
  }));
};

export const useGetCustomerPortalMechanismOptions = () => {
  const translate = useTranslate();

  return Object.values(CustomerPortalSettingsMechanism).map((key) => ({
    value: key,
    label: translate(
      `customer_portal_settings.mechanism.${key}.label` as TranslationKey,
    ),
    hint: translate(
      `customer_portal_settings.mechanism.${key}.description` as TranslationKey,
    ),
  }));
};

export const useGetAllowedWidgetsForPageType = (
  payload: CustomerPortalRequestParams,
) => {
  return useQuery(
    [
      'useGetAllowedWidgetsForPageType',
      payload?.portal?.customerPortalId,
      payload?.pageType,
    ],
    () => CustomerPortalApi.getAllowedWidgetsForPageType(payload),
  );
};

export const useCustomerPortalProjects = (projectId: number | undefined) => {
  return useQuery(
    ['customer-portal-projects', projectId],
    async () => {
      if (!projectId) {
        return [] as Project[];
      }

      return CustomerPortalApi.getCustomerPortalProjects({ projectId });
    },
    { enabled: projectId !== undefined && projectId !== -1 },
  );
};

export const useGetPortalProjectMapping = (projectId: number) => {
  return useQuery(['useGetPortalProjectMapping', projectId], () =>
    CustomerPortalApi.getPortalProjectMapping(projectId),
  );
};


export const useGetDynamicConditionFields = () => {
  return useQuery('dynamicConditionFields', () =>
    CustomerPortalApi.getDynamicConditionFields(),
  );
};

export const useDynamicConditionFilterableFieldsForCustomerPortal = () => {
  const { data: fields = [] } = useGetDynamicConditionFields();

  const filterableFields = useMemo(
    () =>
      getDynamicConditionFields(
        fields.map((f) => ({ ...(f ?? {}), customField: false })),
      ),
    [fields],
  );

  return filterableFields;
};

export const useGetSmartfillFieldsForCustomerPortal = (
  params: CustomerPortalRequestParams | undefined,
) => {
  return useQuery(['useGetSmartfillFieldsForCustomerPortal', params], () =>
    params
      ? CustomerPortalApi.getVariableFields(params)
      : Promise.resolve({
          variableFields: [],
          nestedFields: [],
        }),
  );
};

